import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics, categories, faqs } from '@/mocks/comics';

export default function Home() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCat, setActiveCat] = useState('all');
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredComics = useMemo(() => {
    let result = comics;
    if (searchQuery) {
      result = result.filter(
        (c) =>
          c.title.includes(searchQuery) ||
          c.author.includes(searchQuery) ||
          c.tags.some((t) => t.includes(searchQuery))
      );
    }
    if (activeCat !== 'all') {
      result = result.filter((c) => c.category === activeCat);
    }
    return result;
  }, [searchQuery, activeCat]);

  const hotComics = useMemo(() => [...comics].sort((a, b) => b.views - a.views).slice(0, 8), []);
  const latestComics = useMemo(() => [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 8), []);
  const topRated = useMemo(() => [...comics].sort((a, b) => b.rating - a.rating).slice(0, 8), []);

  return (
    <>
      <SEOHead
        title="免费韩漫大全_好看的韩国漫画_漫画韩漫网址_韩漫在线阅读-韩漫漫画网"
        description="韩漫漫画网提供免费韩漫大全与好看韩国漫画的韩漫在线阅读。通过漫画网址享受专业优质的韩漫服务，浪漫爱情、奇幻玄幻、动作冒险等海量韩漫免费看。"
        keywords="免费韩漫大全,好看韩国漫画,漫画网址,韩漫在线阅读,韩漫漫画网"
        canonicalUrl="https://www.hanmancomic.com"
        ogImage="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20collage%20with%20various%20romance%20fantasy%20action%20comic%20covers%2C%20warm%20vibrant%20color%20palette%2C%20clean%20modern%20layout%2C%20professional%20design&width=1200&height=630&seq=home-og-01&orientation=landscape"
      />
      <SchemaOrg
        type="WebSite"
        data={{
          name: '韩漫漫画网',
          url: 'https://www.hanmancomic.com',
          description: '免费韩漫大全，好看的韩国漫画在线阅读平台',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.hanmancomic.com/?search={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: '韩漫漫画网',
          url: 'https://www.hanmancomic.com',
          description: '免费韩漫大全，好看的韩国漫画在线阅读平台',
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '韩漫漫画网 - 免费韩漫大全',
          description: '韩漫漫画网提供免费韩漫大全与好看韩国漫画的韩漫在线阅读',
          url: 'https://www.hanmancomic.com',
        }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20illustration%20with%20warm%20golden%20amber%20and%20soft%20coral%20gradients%2C%20dreamy%20atmosphere%20with%20floating%20geometric%20shapes%2C%20Korean%20manhwa%20inspired%20decorative%20elements%2C%20clean%20minimal%20composition%2C%20soft%20ethereal%20lighting%2C%20professional%20digital%20art&width=1600&height=700&seq=home-hero-01&orientation=landscape"
            alt="韩漫漫画网 - 免费韩漫大全"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                免费韩漫大全
              </h1>
              <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
                韩漫漫画网为您提供好看的韩国漫画，海量免费韩漫在线阅读。
                浪漫爱情、奇幻玄幻、动作冒险等多种类型，随时随地畅享韩漫阅读体验。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/romance"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-white/20 text-white border border-white/30 rounded-full font-medium text-sm hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              分类浏览
            </h2>
            <Link to="/category/romance" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer">
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors mb-3">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">{cat.name}</h3>
                <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{cat.description}</p>
                <span className="text-[10px] text-foreground-400">{cat.count} 部作品</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                热门推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates + Top Rated */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Latest Updates */}
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                最新更新
              </h2>
              <div className="space-y-3">
                {latestComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-16 h-[88px] rounded-md overflow-hidden flex-shrink-0">
                      <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 mb-1">{comic.title}</h3>
                      <p className="text-xs text-foreground-500 mb-2">{comic.author} · {comic.categoryName}</p>
                      <div className="flex items-center gap-2 text-[10px] text-foreground-400">
                        <span>更新至 {comic.chapters} 话</span>
                        <span>·</span>
                        <span>{comic.updateDate}</span>
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                      {comic.status}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            {/* Top Rated */}
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                评分最高
              </h2>
              <div className="space-y-3">
                {topRated.map((comic, index) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="w-12 h-[68px] rounded-md overflow-hidden flex-shrink-0">
                      <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 mb-1">{comic.title}</h3>
                      <div className="flex items-center gap-1 text-xs">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-accent-700 font-medium">{comic.rating}</span>
                        <span className="text-foreground-400 ml-1">{(comic.views / 10000).toFixed(0)}万人气</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="max-w-2xl mx-auto space-y-3">
              <SchemaOrg
                type="FAQPage"
                data={{
                  mainEntity: faqs.map((faq) => ({
                    '@type': 'Question',
                    name: faq.question,
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: faq.answer,
                    },
                  })),
                }}
              />
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                        faqOpen === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {faqOpen === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            立即开始你的韩漫之旅
          </h2>
          <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
            海量免费韩漫等你来发现，随时随地开启精彩阅读体验
          </p>
          <Link
            to="/category/romance"
            className="inline-block px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            立即浏览
          </Link>
        </section>
      </main>

      <Footer />
    </>
  );
}